import { Component, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

import { DocumentService } from '../../../../shared/data-access/services';
import { OfficeGetFileState } from '../../models/office.model';
import { Attachment } from '../../models/attachment.model';
import { NotificationService } from '../../services/notification.service';
import { FileUtilsService } from '../../services/fileUtils.service';
import { DocumentType } from '../../../../shared/data-access/models';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'swisscom-sign-file-reader',
  templateUrl: './file-reader.component.html',
  styleUrls: ['./file-reader.component.scss'],
  standalone: false,
})
export class FileReaderComponent implements OnInit {
  private readonly documentService = inject(DocumentService);
  private readonly translateService = inject(TranslateService);
  private readonly fileUtils = inject(FileUtilsService);
  private readonly notificationService = inject(NotificationService);

  public readonly inProgress$ = new BehaviorSubject<boolean>(false);

  private pdfSlices: Uint8Array[] = [];
  private documentName = 'Untitled Document';
  private selectedAttachments: Attachment[] = [];
  public isOutlookHost = false;
  public hasError = false;
  public uploadSuccess = false;
  private readonly SLICE_SIZE = 65535; // Limitation for iOS compatibility.
  private dialog?: Office.Dialog;

  public ngOnInit(): void {
    Office.onReady()
      .then(() => {
        if (Office.context.host === Office.HostType.Outlook) {
          this.isOutlookHost = true;
        } else {
          this.loadDocumentName();
        }
      })
      .catch((error) => {
        console.error('Error loading Office.js:', error);
        this.notificationService.showErrorNotification('notification-container', 'App.Notifications.OfficeError');
      });
  }

  private loadDocumentName(): void {
    try {
      Office.context.document.getFilePropertiesAsync((result) => {
        const fileUrl = result.value.url;
        if (!fileUrl) {
          this.translateService.get('App.FileReader.defaultDocumentName').subscribe((translation: string) => {
            this.documentName = translation;
          });
        } else {
          this.documentName = fileUrl.substring(
            Math.max(fileUrl.lastIndexOf('/'), fileUrl.lastIndexOf('\\')) + 1,
            fileUrl.lastIndexOf('.')
          );
        }
      });
    } catch (error) {
      console.error('Error loading document name:', error);
      this.notificationService.showErrorNotification('notification-container', 'App.Notifications.DocumentLoadError');
    }
  }

  private loadAndUploadDocument(): void {
    if (Office.context.host === Office.HostType.Outlook && this.selectedAttachments.length > 0) {
      this.uploadSelectedAttachments();
    } else {
      this.loadAndUploadOfficeDocument();
    }
  }

  private uploadSelectedAttachments(): void {
    this.inProgress$.next(true);

    const readPromises = this.selectedAttachments.map(
      (attachment) =>
        new Promise<{ blob: Blob; documentType: DocumentType; fileName: string } | null>((resolve) => {
          const item = Office.context.mailbox.item;
          if (!item) {
            resolve(null);
            return;
          }

          item.getAttachmentContentAsync(attachment.id, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const byteCharacters = atob(result.value.content);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }

              const fileExtension = attachment.name.split('.').pop()?.toLowerCase();
              const documentType = this.fileUtils.getDocumentType(fileExtension) as DocumentType;
              const mimeType = this.fileUtils.getMimeType(fileExtension);
              const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });

              resolve({ blob, documentType, fileName: attachment.name });
            } else {
              console.error('Error reading attachment:', result.error.message);
              resolve(null);
            }
          });
        })
    );

    Promise.all(readPromises).then((attachmentData) => {
      const validAttachments = attachmentData.filter((a): a is NonNullable<typeof a> => a !== null);

      if (validAttachments.length === 0) {
        this.inProgress$.next(false);
        this.notificationService.showErrorNotification(
          'notification-container',
          'App.Notifications.AttachmentReadError'
        );
        this.hasError = true;
        return;
      }

      const body = {
        File: validAttachments.map((a) => a.blob),
        FileName: validAttachments.map((a) => a.fileName),
        DocumentType: validAttachments.map((a) => a.documentType),
      };

      this.documentService.upload({ body }).subscribe({
        next: (response) => {
          this.inProgress$.next(false);

          if (!response?.pdfUrl) {
            console.error('Error retrieving Ajila URL.');
            this.notificationService.showErrorNotification('notification-container', 'App.Notifications.UploadError');
            this.hasError = true;
            return;
          }

          this.uploadSuccess = true;
          this.notificationService.showSuccessNotificationWithLink(
            'notification-container',
            'App.Notifications.UploadSuccess',
            response.pdfUrl,
            () => this.onUploadAgain()
          );
          this.openSigningDialog(response.pdfUrl);
        },
        error: (err) => {
          console.error('Error uploading files:', err);
          this.inProgress$.next(false);
          this.hasError = true;

          if (err.status === 429) {
            this.notificationService.showErrorNotification(
              'notification-container',
              'App.Notifications.UploadError429'
            );
          } else {
            this.notificationService.showErrorNotification(
              'notification-container',
              'App.Notifications.UploadError',
              err.status
            );
          }
        },
      });
    });
  }

  private loadAndUploadOfficeDocument(): void {
    Office.context.document.getFileAsync(Office.FileType.Pdf, { sliceSize: this.SLICE_SIZE }, (fileResult) => {
      this.pdfSlices = [];
      if (fileResult.status === Office.AsyncResultStatus.Succeeded) {
        const state: OfficeGetFileState = {
          file: fileResult.value,
          currentSlice: 0,
        };
        this.getNextSlice(state);
      } else {
        console.error('Error loading Office document:', fileResult.error.message);
        this.notificationService.showErrorNotification('notification-container', 'App.Notifications.DocumentReadError');
        this.hasError = true;
      }
    });
  }

  private uploadFile(file: Blob, documentType: DocumentType = DocumentType.Pdf): void {
    const fileName = this.documentName + '.pdf';
    const requestBody = {
      File: [file],
      FileName: [fileName],
      DocumentType: [documentType],
    };

    if (!environment.production) {
      console.group('API Upload Debug');
      console.log('Endpoint:', `${this.documentService.rootUrl}${DocumentService.UploadPath}`);
      console.log('File details:', {
        name: fileName,
        sizeInBytes: file.size,
        mimeType: file.type,
        documentType,
      });
      console.groupEnd();
    }

    this.inProgress$.next(true);
    this.documentService
      .upload({
        body: requestBody,
      })
      .subscribe({
        next: (response) => {
          this.inProgress$.next(false);
          if (!response?.pdfUrl) {
            console.error('Error retrieving Ajila URL.');
            this.notificationService.showErrorNotification('notification-container', 'App.Notifications.UploadError');
          } else {
            this.uploadSuccess = true;
            this.notificationService.showSuccessNotificationWithLink(
              'notification-container',
              'App.Notifications.UploadSuccess',
              response.pdfUrl,
              () => this.onUploadAgain()
            );
            this.openSigningDialog(response.pdfUrl);
          }
        },
        error: (err) => {
          console.error('Error uploading file:', err);
          this.inProgress$.next(false);

          if (err.status === 429) {
            this.notificationService.showErrorNotification(
              'notification-container',
              'App.Notifications.UploadError429'
            );
          } else {
            this.notificationService.showErrorNotification(
              'notification-container',
              'App.Notifications.UploadError',
              err.status
            );
          }
        },
      });
  }

  /**
   * Office only allows opening dialogs on the add-in's own domain, so we open a same-origin
   * redirect page that forwards to the (external) signing URL.
   */
  private openSigningDialog(url: string): void {
    this.dialog?.close();
    this.dialog = undefined;

    const redirectUrl = new URL('assets/dialog-redirect.html', document.baseURI);
    redirectUrl.searchParams.set('target', url);

    Office.context.ui.displayDialogAsync(redirectUrl.href, { height: 80, width: 80 }, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        console.error('Error opening signing dialog:', result.error.code, result.error.message);
        return;
      }
      this.dialog = result.value;
      this.dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        this.dialog = undefined;
      });
    });
  }

  private getNextSlice(state: OfficeGetFileState): void {
    state.file.getSliceAsync(state.currentSlice, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        this.pdfSlices.push(new Uint8Array(result.value.data));
        if (state.file.sliceCount <= ++state.currentSlice) {
          state.file.closeAsync();
          this.onGotAllSlices();
        } else {
          this.getNextSlice(state);
        }
      } else {
        console.error('Error loading file slice', state.currentSlice + 1 + ':', result.error.message);
        this.notificationService.showErrorNotification('notification-container', 'App.Notifications.SliceError');
        this.hasError = true;
      }
    });
  }

  private onGotAllSlices(): void {
    this.uploadFile(new Blob(this.pdfSlices as BlobPart[], { type: 'application/pdf' }));
    this.pdfSlices = [];
  }

  public onSelectedAttachmentsChange(attachments: Attachment[]): void {
    this.selectedAttachments = attachments;
  }

  public isFileSelected(): boolean {
    return this.selectedAttachments.length > 0;
  }

  public onSubmit(): void {
    this.loadAndUploadDocument();
  }

  private onUploadAgain(): void {
    this.uploadSuccess = false;
    this.hasError = false;
    const container = document.getElementById('notification-container');
    if (container) {
      container.innerHTML = '';
    }
    this.loadAndUploadDocument();
  }
}
